'use client';

import Image from 'next/image';
import { Fragment, useState } from 'react';

import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { staticMediaUrl } from '@/fo/util/image-loader';

import { styles } from './Help.styles';

const CDN = staticMediaUrl('landing/bioderma_galchonok');

type TabImage = string | { desktop: string; mobile: string };

const isResponsiveImage = (
  image: TabImage
): image is { desktop: string; mobile: string } => typeof image === 'object';

const getImageSrc = (image: TabImage) =>
  typeof image === 'string' ? image : image.desktop;

const titleToAlt = (title: string) =>
  title
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const renderTitle = (title: string) =>
  title.split(/<br\s*\/?>/i).map((line, index) => (
    <Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));

const tabs = [
  {
    id: 'action',
    image: `${CDN}/tab1.png`,
    title: 'Ежегодная<br />акция',
    text: '<b>Помочь тем, кто в этом нуждается:</b> бренд BIODERMA ежегодно проводит благотворительную акцию в пользу Фонда «ГАЛЧОНОК». В период акции можно помочь подопечным благотворительного фонда, купив одно из средств гаммы бренда BIODERMA.<br/>Средства с акции направляются на системную поддержку детей и молодежи с органическими поражениями центральной нервной системы (преимущественно ЦП): услуги по социальной абилитации, подбор и оплату технических средств реабилитации, Федеральную горячую линию по ДЦП «ГАЛЧОНОК» и проект генетических исследований «Шаг вперед».<br/><b>Проект «Шаг вперед»</b> помогает детям с симптомами, похожими на ДЦП, пройти молекулярно-генетическую диагностику, чтобы выявить возможные наследственные заболевания. Семьям предоставляется полногеномное секвенирование с&nbsp;расшифровкой&nbsp;результатов.',
    images: [
      {
        desktop: `${CDN}/action2_new2.png`,
        mobile: `${CDN}/action2_new_sm2.png`
      }
    ]
  },
  {
    id: 'kids',
    image: `${CDN}/tab2.png`,
    title: 'Детские<br />мероприятия',
    text: 'Сотрудничество BIODERMA и Фонда давно вышло за рамки ежегодной акции. Мы реализуем инициативы, направленные&nbsp;не только на системную финансовую помощь детям, но и эмоциональную. Все активности направлены на то, чтобы сделать дни для детей особенно запоминающимися: детские мероприятия, финансирование поездок в&nbsp;детские&nbsp;лагеря, реализация новогодних желаний подопечных фонда.',
    images: [
      `${CDN}/kids-card1.jpg`,
      `${CDN}/kids-card2.jpg`,
      `${CDN}/kids-card3.jpg`,
      `${CDN}/kids-card4.jpg`
    ]
  },
  {
    id: 'moms',
    image: `${CDN}/tab3.png`,
    title: 'Дни красоты<br />для мам',
    text: '«День красоты» — камерное событие заботы о себе для мам и женщин из семей подопечных фонда «ГАЛЧОНОК», который помогает детям и молодежи с органическими поражениями центральной нервной системы (преимущественно церебральный паралич). Целью мероприятия было создание пространства для отдыха от повседневных забот, где участницы смогут перезагрузиться и восстановить внутренний ресурс',
    images: [
      `${CDN}/mom-card1.jpg`,
      `${CDN}/mom-card2.jpg`,
      `${CDN}/mom-card3.jpg`,
      `${CDN}/mom-card4.jpg`,
      `${CDN}/mom-card5.jpg`,
      `${CDN}/mom-card6.jpg`,
      `${CDN}/mom-card7.png`
    ]
  }
];

export function Help({ className }: { className?: string }) {
  const [activeTab, setActiveTab] = useState(0);
  const slots = styles();
  const currentTab = tabs[activeTab] ?? tabs[0];
  const imageAlt = titleToAlt(currentTab.title);

  return (
    <div
      className={slots.base({ className })}
      id="help"
    >
      <h3 className={slots.title()}>Помогать просто</h3>
      <ul className={slots.tabs()}>
        {tabs.map((tab, index) => (
          <li key={tab.id}>
            <button
              type="button"
              className={slots.tab({ isActive: index === activeTab })}
              aria-pressed={index === activeTab}
              onClick={() => setActiveTab(index)}
            >
              <Image
                src={tab.image}
                alt={titleToAlt(tab.title)}
                width={80}
                height={80}
              />
              <p className={slots.tabTitle()}>{renderTitle(tab.title)}</p>
            </button>
          </li>
        ))}
      </ul>
      <div className={slots.content()}>
        <p
          className={slots.text({
            textWidth: currentTab.id === 'action' ? 'wide' : 'narrow'
          })}
          dangerouslySetInnerHTML={{ __html: currentTab.text }}
        />
        {currentTab.images.length > 1 ? (
          <UiKitCarouselBase
            key={currentTab.id}
            className={slots.carousel()}
            arrowPrevClass={slots.arrowPrev()}
            arrowNextClass={slots.arrowNext()}
            loop={true}
            alwaysShowArrows
          >
            {currentTab.images.map((image) => (
              <Image
                key={getImageSrc(image)}
                className={slots.carouselImage()}
                src={getImageSrc(image)}
                alt={imageAlt}
                width={400}
                height={400}
              />
            ))}
          </UiKitCarouselBase>
        ) : isResponsiveImage(currentTab.images[0]) ? (
          <picture className={slots.imageLarge()}>
            <source
              srcSet={currentTab.images[0].mobile}
              media="(max-width: 767px)"
            />
            <img
              className="mx-auto h-auto w-full max-w-[860px] overflow-hidden rounded-[20px] lg:rounded-[32px]"
              src={currentTab.images[0].desktop}
              alt={imageAlt}
            />
          </picture>
        ) : (
          <Image
            className={slots.imageLarge()}
            src={currentTab.images[0]}
            alt={imageAlt}
            width={600}
            height={600}
          />
        )}
      </div>
    </div>
  );
}
